/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { DimensionScaler } from '../../../../core/device/DimensionScaler';
import { DESCRIPTION_CONTAINER_HEIGHT } from '../onelineguide.constants';
import { PreviewDescriptionOneLineGuideProps } from './PreviewDescriptionOneLineGuideProps';

import './PreviewDescriptionOneLineGuide.scss';

const PreviewDescriptionOneLineGuide: React.FC<PreviewDescriptionOneLineGuideProps> = ({
    description,
    details,
}): JSX.Element => {
    const descriptionRef = useRef<HTMLDivElement>(null);
    const detailsRef = useRef<HTMLDivElement>(null);
    const [shouldTrimDescription, setShouldTrimDescription] = useState(false);
    const [shouldMeasure, setShouldMeasure] = useState(true);

    useEffect(() => {
        setShouldMeasure(true);
    }, [description]);

    useLayoutEffect(() => {
        if (!shouldMeasure) {
            return;
        }

        const descriptionRefElementHeight = descriptionRef.current
            ? descriptionRef.current.clientHeight
            : 0;

        setShouldTrimDescription(
            Math.ceil(descriptionRefElementHeight) >
                DimensionScaler.getInstance().getScaledWidth(DESCRIPTION_CONTAINER_HEIGHT),
        );

        setShouldMeasure(false);
    }, [shouldMeasure]);

    const previewContainerClass = `olg-show-description-container ${!shouldMeasure && shouldTrimDescription ? 'has-trimmed-description' : ''}`;

    const calculateMaskSizeClass = (detailsString: string): string => {
        if (detailsString.length > 7 && detailsString.length < 12) {
            return 'mask-size-small';
        }
        if (detailsString.length >= 12 && detailsString.length < 15) {
            return 'mask-size-medium';
        }
        if (detailsString.length >= 15) {
            return 'mask-size-large';
        }
        return '';
    };

    const maskSizeClassName = calculateMaskSizeClass(details);

    return (
        <div className={previewContainerClass}>
            <span
                className={`olg-show-description ${maskSizeClassName}`}
                data-show-details={details}
                ref={descriptionRef}
            >
                {description}
            </span>
            <span className="olg-show-details" ref={detailsRef}>
                {details}
            </span>
        </div>
    );
};

export default PreviewDescriptionOneLineGuide;
